/**
 * PicksView — Match prediction feed: one scroll grouped by stage, then by kickoff date
 * (same date header pattern as fixtures-by-league). Upcoming / Finished pill tabs; FlashList.
 */

import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { View, Text, Image, Pressable, useWindowDimensions } from 'react-native';
import { useRouter } from 'expo-router';
import { WifiOff } from 'lucide-react-native';
import { FlashList, ListRenderItemInfo } from '@shopify/flash-list';
import { FLASHLIST_ANDROID_OVERRIDE_PROPS } from '../../../src/shared/constants/flashList.constants';
import { useTheme } from '../../../src/theme/useTheme';
import { useSupabaseAuth } from '../../../src/shared/contexts/SupabaseAuthContext';
import { useCompetitionBasePath } from '../../../src/competitions/contexts/CompetitionContext';
import {
  competitionMatchRoute,
  competitionMatchStatsRoute,
} from '../../../src/competitions/helpers/competitionRoutes.helper';
import { useAccountSwitcher } from '../../../src/accounts/contexts/AccountSwitcherContext';
import { useAvatarContext } from '../../../src/avatars/contexts/AvatarContext';
import type { DisplayAvatarId } from '../../../src/avatars/types/avatar.types';
import { usePostGameResultOverlay } from '../../../src/worldcup/hooks/usePostGameResultOverlay';
import { PostGameResultOverlay } from '../PostGameResultOverlay/PostGameResultOverlay';
import {
  ReviewPromptModal,
  type ReviewPromptCloseReason,
} from '../../../src/shared/components/ReviewPromptModal/ReviewPromptModal';
import { requestStoreReview } from '../../../src/shared/helpers/requestStoreReview.helper';
import { useAnalytics } from '../../../src/analytics/useAnalytics';
import { ANALYTICS_EVENTS } from '../../../src/analytics/events';
import { useWorldCupPicksData } from '../../../src/worldcup/hooks/useWorldCupPicksData';
import { useSkeletonLoading } from '../../../src/shared/hooks/useSkeletonLoading';
import { useDialog } from '../../../src/shared/hooks/useDialog';
import { ARENA_EMPTY_MONKEY_SOURCE } from '../../../constants/assets';
import { FadeInContent } from '../../shared/FadeInContent';
import { StaggeredFadeIn } from '../../shared/StaggeredFadeIn';
import { AnimatedSegmentedControl } from '../../ui/AnimatedSegmentedControl';
import { WorldCupSkeleton } from '../WorldCupSkeleton/WorldCupSkeleton';
import { getPicksViewStyles } from './PicksView.styles';
import { MatchCard } from '../MatchesView/MatchCard';
import { LateJoinBanner } from '../../../src/worldcup/components/LateJoinBanner/LateJoinBanner';
import { GuestBanner } from '../../../src/shared/components/GuestBanner/GuestBanner';
import { isLateJoiner, isMatchMissedPreJoin } from '../../../src/worldcup/helpers/worldcupLateJoin.helper';
import { FuturesCard } from './FuturesCard';
import { AutofillPicksButton } from './AutofillPicksButton';
import {
  PICKS_TIME_TAB,
  getPicksEmptySubtitle,
  PICKS_VIEW_COPY,
  AUTOFILL_PICKS_COPY,
  ITEM_TYPE_HEADER,
  ITEM_TYPE_DATE_HEADER,
  ITEM_TYPE_MATCH,
  TIME_TABS,
  type PicksTimeTabKey,
} from './PicksView.constants';
import {
  flattenPicksStageSectionsForFlashList,
  type PicksFlashListItem,
} from '../../../src/worldcup/helpers/worldcupPicksSections.helper';

interface PicksViewProps {
  /**
   * Optional content rendered at the very top of the scrollable feed, above the
   * futures card. Used by the self profile to bolt a stats header onto the
   * editable picks feed without duplicating the feed itself.
   */
  headerSlot?: React.ReactNode;
}

export const PicksView: React.FC<PicksViewProps> = ({ headerSlot }) => {
  const theme = useTheme();
  const router = useRouter();
  const basePath = useCompetitionBasePath();
  const { width: windowWidth } = useWindowDimensions();
  const styles = useMemo(() => getPicksViewStyles(theme, windowWidth), [theme, windowWidth]);
  const { userId } = useSupabaseAuth();
  const { accounts, activeUserId } = useAccountSwitcher();
  const activeSession = useMemo(
    () => accounts.find((a) => a.userId === activeUserId) ?? null,
    [accounts, activeUserId],
  );
  // Live avatar from AvatarContext (no storage delay); persisted session is the fallback.
  const { currentAvatarId, currentAvatarConfig } = useAvatarContext();
  const overlayAvatarId = (currentAvatarId ??
    activeSession?.zportiAvatarId ??
    null) as DisplayAvatarId | null;
  const overlayAvatarConfig = currentAvatarConfig ?? activeSession?.zportiAvatarConfig ?? null;
  const {
    upcomingStageSections,
    finishedStageSections,
    matches,
    isLoading,
    isError,
    predictions,
    onPredictionChange,
    autofillableMonkeyPickCount,
    autofillMonkeyPicks,
    onToggleBoost,
    isBoosted,
    isBoostLockedByOtherMatch,
    isBoostEligible,
    futures,
    futuresLocked,
    saveFutures,
    futuresOptions,
  } = useWorldCupPicksData();
  const { showDialog, showAlert } = useDialog();
  const { track } = useAnalytics();
  const [activeTimeTab, setActiveTimeTab] = useState<PicksTimeTabKey>(PICKS_TIME_TAB.UPCOMING);
  const {
    isOverlayVisible,
    currentResult,
    dismissCurrent,
    isReviewPromptVisible,
    closeReviewPrompt,
  } = usePostGameResultOverlay({
    matches,
    userId,
    isDataReady: !isLoading && !isError,
  });

  // One-time review prompt (specs/review-prompt): shown after a winning last result.
  useEffect(() => {
    if (isReviewPromptVisible) {
      track(ANALYTICS_EVENTS.REVIEW_PROMPT_SHOWN, { source: 'worldcup_post_game' });
    }
  }, [isReviewPromptVisible, track]);

  const handleReviewAccept = useCallback(async () => {
    track(ANALYTICS_EVENTS.REVIEW_PROMPT_ACCEPTED, { source: 'worldcup_post_game' });
    await requestStoreReview();
  }, [track]);

  const handleReviewClose = useCallback(
    (reason: ReviewPromptCloseReason) => {
      if (reason === 'dismissed') {
        track(ANALYTICS_EVENTS.REVIEW_PROMPT_DISMISSED, { source: 'worldcup_post_game' });
      }
      closeReviewPrompt();
    },
    [closeReviewPrompt, track],
  );
  const [isAutofilling, setIsAutofilling] = useState(false);
  const [lateBannerDismissed, setLateBannerDismissed] = useState(false);
  // Late joiner: they hold no pick for matches that kicked off before they joined (forfeited → 0).
  const showLateJoinBanner = useMemo(
    () => !lateBannerDismissed && isLateJoiner(matches),
    [lateBannerDismissed, matches],
  );

  const handleAutofillPress = useCallback(async () => {
    if (isAutofilling) return;
    const confirmed = await showDialog({
      title: AUTOFILL_PICKS_COPY.CONFIRM_TITLE,
      message: AUTOFILL_PICKS_COPY.CONFIRM_MESSAGE,
      confirmText: AUTOFILL_PICKS_COPY.CONFIRM_CTA,
      cancelText: AUTOFILL_PICKS_COPY.CONFIRM_CANCEL,
    });
    if (!confirmed) return;
    setIsAutofilling(true);
    try {
      await autofillMonkeyPicks();
    } catch {
      await showAlert({
        title: AUTOFILL_PICKS_COPY.ERROR_TITLE,
        message: AUTOFILL_PICKS_COPY.ERROR_MESSAGE,
        type: 'default',
      });
    } finally {
      setIsAutofilling(false);
    }
  }, [autofillMonkeyPicks, isAutofilling, showAlert, showDialog]);


  const activeStageSections = useMemo(
    () => (activeTimeTab === PICKS_TIME_TAB.UPCOMING ? upcomingStageSections : finishedStageSections),
    [activeTimeTab, upcomingStageSections, finishedStageSections],
  );

  const { data: flashListData, stickyHeaderIndices } = useMemo(
    () => flattenPicksStageSectionsForFlashList(activeStageSections),
    [activeStageSections],
  );

  const hasMatches = matches.length > 0;
  const tabIsEmpty = hasMatches && flashListData.length === 0;

  const { isLoadingWithDelay, contentOpacity } = useSkeletonLoading({
    isLoading,
    isContentReady: !isLoading,
  });

  const getItemType = useCallback((item: PicksFlashListItem) => {
    if (item.type === 'header') return ITEM_TYPE_HEADER;
    if (item.type === 'dateHeader') return ITEM_TYPE_DATE_HEADER;
    return ITEM_TYPE_MATCH;
  }, []);

  // Built from the competition base path, not the World Cup literal: this feed is also the
  // self-profile's editable list, which the generic competition shell mounts (AC-10.10).
  // With no provider the context returns the World Cup value, so the WC picks tab is unchanged.
  const handleFixturePress = useCallback(
    (gameId: string) => {
      router.push(competitionMatchRoute(basePath, gameId) as never);
    },
    [router, basePath],
  );

  const handleStatsPress = useCallback(
    (matchId: string) => {
      router.push(competitionMatchStatsRoute(basePath, matchId) as never);
    },
    [router, basePath],
  );

  const keyExtractor = useCallback((item: PicksFlashListItem) => {
    if (item.type === 'header') {
      return `h-${activeTimeTab}-${item.stageKey}`;
    }
    if (item.type === 'dateHeader') {
      return `dh-${activeTimeTab}-${item.stageKey}-${item.dateKey}`;
    }
    return `m-${item.match.id}`;
  }, [activeTimeTab]);

  const renderItem = useCallback(
    ({ item, index: listIndex }: ListRenderItemInfo<PicksFlashListItem>) => {
      if (item.type === 'header') {
        return (
          <StaggeredFadeIn index={listIndex} disableAnimation>
            <View style={styles.sectionHeaderRow}>
              <View style={styles.sectionHeaderAccent} accessible={false} />
              <Text style={styles.sectionHeader}>{item.label}</Text>
            </View>
          </StaggeredFadeIn>
        );
      }
      if (item.type === 'dateHeader') {
        return (
          <StaggeredFadeIn index={listIndex} disableAnimation>
            <View style={styles.dateHeader}>
              <Text style={styles.dateHeaderText} numberOfLines={1}>{item.title}</Text>
            </View>
          </StaggeredFadeIn>
        );
      }
      return (
        // disableAnimation: MatchCard rows render inside a recycling FlashList.
        // Per-row native-driver entrance animations re-fire on every cell recycle
        // and have been implicated in Android 16 Fabric mount races
        // (RetryableMountingLayerException "Unable to find viewState for tag").
        // Headers above already opt out; the container still fades in via FadeInContent.
        <StaggeredFadeIn index={listIndex} disableAnimation>
          <MatchCard
            match={item.match}
            predictionInput={predictions[item.match.id]}
            onPredictionChange={onPredictionChange}
            isBoosted={isBoosted(item.stageKey, item.match.id)}
            isBoostLocked={isBoostLockedByOtherMatch(item.stageKey, item.match.id)}
            isBoostEligible={isBoostEligible(item.stageKey)}
            onToggleBoost={() => onToggleBoost(item.stageKey, item.match.id)}
            onFixturePress={handleFixturePress}
            onStatsPress={handleStatsPress}
            isMissedPreJoin={isMatchMissedPreJoin(item.match)}
          />
        </StaggeredFadeIn>
      );
    },
    [
      styles.sectionHeaderRow,
      styles.sectionHeaderAccent,
      styles.sectionHeader,
      styles.dateHeader,
      styles.dateHeaderText,
      predictions,
      onPredictionChange,
      onToggleBoost,
      isBoosted,
      isBoostLockedByOtherMatch,
      isBoostEligible,
      handleFixturePress,
      handleStatsPress,
    ],
  );

  const listEmpty = useMemo(() => {
    if (!hasMatches) {
      return (
        <View style={styles.listEmptyWrap}>
          <Image source={ARENA_EMPTY_MONKEY_SOURCE} style={styles.emptyMonkey} resizeMode="contain" />
          <Text style={styles.listEmptyTitle}>{PICKS_VIEW_COPY.EMPTY_TITLE}</Text>
          <Text style={styles.listEmptySubtitle}>{getPicksEmptySubtitle()}</Text>
        </View>
      );
    }
    if (activeTimeTab === PICKS_TIME_TAB.UPCOMING) {
      return (
        <View style={styles.listEmptyWrap}>
          <Image source={ARENA_EMPTY_MONKEY_SOURCE} style={styles.emptyMonkey} resizeMode="contain" />
          <Text style={styles.listEmptyTitle}>{PICKS_VIEW_COPY.EMPTY_UPCOMING_TITLE}</Text>
          <Text style={styles.listEmptySubtitle}>{PICKS_VIEW_COPY.EMPTY_UPCOMING_SUBTITLE}</Text>
        </View>
      );
    }
    return (
      <View style={styles.listEmptyWrap}>
        <Image source={ARENA_EMPTY_MONKEY_SOURCE} style={styles.emptyMonkey} resizeMode="contain" />
        <Text style={styles.listEmptyTitle}>{PICKS_VIEW_COPY.EMPTY_FINISHED_TITLE}</Text>
        <Text style={styles.listEmptySubtitle}>{PICKS_VIEW_COPY.EMPTY_FINISHED_SUBTITLE}</Text>
      </View>
    );
  }, [activeTimeTab, hasMatches, styles]);

  const listHeader = useMemo(
    () => (
      <View style={styles.topStack}>
        {headerSlot ? <View style={styles.headerSlotWrap}>{headerSlot}</View> : null}
        <View style={styles.listHeaderBody}>
          <GuestBanner />
          {showLateJoinBanner && (
            <LateJoinBanner onDismiss={() => setLateBannerDismissed(true)} />
          )}
          <FuturesCard
            futures={futures}
            futuresLocked={futuresLocked}
            futuresOptions={futuresOptions}
            onSave={saveFutures}
          />
          <View style={styles.pillsRow}>
            <AnimatedSegmentedControl<PicksTimeTabKey>
              options={TIME_TABS}
              value={activeTimeTab}
              onChange={setActiveTimeTab}
              fullWidth
              testID="worldcup-picks-time-toggle"
            />
          </View>
          {activeTimeTab === PICKS_TIME_TAB.UPCOMING && autofillableMonkeyPickCount > 0 && (
            <AutofillPicksButton
              count={autofillableMonkeyPickCount}
              isBusy={isAutofilling}
              onPress={handleAutofillPress}
            />
          )}
        </View>
      </View>
    ),
    [
      activeTimeTab,
      autofillableMonkeyPickCount,
      isAutofilling,
      handleAutofillPress,
      futures,
      futuresLocked,
      futuresOptions,
      saveFutures,
      styles,
      headerSlot,
      showLateJoinBanner,
    ],
  );

  if (isLoadingWithDelay) {
    return <WorldCupSkeleton variant="picks" />;
  }

  if (isError) {
    return (
      <View style={[styles.container, styles.errorContainer]}>
        <View style={styles.errorIconWrap}>
          <WifiOff size={48} color={theme.gray[400]} />
        </View>
        <Text style={styles.errorTitle}>{PICKS_VIEW_COPY.ERROR_TITLE}</Text>
        <Text style={styles.errorSubtitle}>{PICKS_VIEW_COPY.ERROR_SUBTITLE}</Text>
        <Pressable style={styles.errorButton} onPress={() => router.replace('/app/worldcup' as never)}>
          <Text style={styles.errorButtonLabel}>{PICKS_VIEW_COPY.ERROR_RETRY}</Text>
        </Pressable>
      </View>
    );
  }

  return (
    <FadeInContent opacity={contentOpacity} style={styles.container}>
      <PostGameResultOverlay
        isVisible={isOverlayVisible}
        result={currentResult}
        avatarId={overlayAvatarId}
        avatarConfig={overlayAvatarConfig}
        avatarFallbackUrl={activeSession?.avatarUrl ?? undefined}
        onDismiss={dismissCurrent}
      />
      <ReviewPromptModal
        visible={isReviewPromptVisible}
        onAccept={handleReviewAccept}
        onClose={handleReviewClose}
      />
      <FlashList<PicksFlashListItem>
        overrideProps={FLASHLIST_ANDROID_OVERRIDE_PROPS}
        style={styles.list}
        contentContainerStyle={styles.listContent}
        data={flashListData}
        ListHeaderComponent={listHeader}
        ListEmptyComponent={tabIsEmpty || !hasMatches ? listEmpty : undefined}
        stickyHeaderIndices={stickyHeaderIndices.length > 0 ? stickyHeaderIndices : undefined}
        getItemType={getItemType}
        keyExtractor={keyExtractor}
        renderItem={renderItem}
        showsVerticalScrollIndicator={false}
      />
    </FadeInContent>
  );
};
